/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { Button, IncrementalQuantity, Popover } from '@msdyn365-commerce-modules/utilities';
import React from 'react';

/**
 * Resources for the enter all quantities component.
 */
export interface IProductDimensionsMatrixEnterAllQuantitiesResources {
    productDimensionsMatrixInputQuantityAriaLabel: string;
    productDimensionsMatrixEnterAllQuantitiesButtonText: string;
    productDimensionsMatrixEnterAllQuantitiesDecrementButtonAriaLabel: string;
    productDimensionsMatrixEnterAllQuantitiesIncrementButtonAriaLabel: string;
    productDimensionsMatrixEnterAllQuantitiesQuantityText: string;
    productDimensionsMatrixEnterAllQuantitiesEachText: string;
    productDimensionsMatrixEnterAllQuantitiesApplyToAllText: string;
}

/**
 * Props for the enter all quantities component.
 */
export interface IProductDimensionsMatrixEnterAllQuantitiesProps {
    className: string;

    resources: IProductDimensionsMatrixEnterAllQuantitiesResources;

    defaultQuantity: number;
    minQuantityValue: number;
    maxQuantityValue: number;

    onApplyToAll(newQuantity: number): void;
}

/**
 * Popover content for the enter all quantities component.
 * @param props - Enter all quantities component props.
 * @returns React node with the content to be used in the popover.
 */
const ProductDimensionsMatrixEnterAllQuantitiesPopoverContentComponent: React.FC<IProductDimensionsMatrixEnterAllQuantitiesProps> = (
    props: IProductDimensionsMatrixEnterAllQuantitiesProps) => {

    const [currentQuantity, setCurrentQuantity] = React.useState(props.defaultQuantity);

    const handleQuantityChange = React.useCallback((newQuantity?: number) => {
        setCurrentQuantity(newQuantity ?? props.defaultQuantity);
        return true;
    }, [props.defaultQuantity]);

    const handleApplyAll = React.useCallback(() => {
        props.onApplyToAll(currentQuantity);
        return true;
    }, [props, currentQuantity]);

    return (
        <div className={props.className}>
            <div className={`${props.className}__quantity-label`}>
                {props.resources.productDimensionsMatrixEnterAllQuantitiesQuantityText}
            </div>
            <IncrementalQuantity
                currentCount={props.defaultQuantity}
                onChange={handleQuantityChange}
                inputQuantityAriaLabel={props.resources.productDimensionsMatrixInputQuantityAriaLabel}
                min={props.minQuantityValue}
                max={props.maxQuantityValue}
                decrementButtonAriaLabel={props.resources.productDimensionsMatrixEnterAllQuantitiesDecrementButtonAriaLabel}
                incrementButtonAriaLabel={props.resources.productDimensionsMatrixEnterAllQuantitiesIncrementButtonAriaLabel}
            />
            <div className={`${props.className}__button-label`}>
                {props.resources.productDimensionsMatrixEnterAllQuantitiesEachText}
            </div>
            <div className={`${props.className}__apply-to-all-container`}>
                <Button
                    className={`${props.className}__apply-to-all-container__button`}
                    title={props.resources.productDimensionsMatrixEnterAllQuantitiesApplyToAllText}
                    onClick={handleApplyAll}
                >
                    {props.resources.productDimensionsMatrixEnterAllQuantitiesApplyToAllText}
                </Button>
            </div>
        </div>
    );
};

/**
 * Enter all quantities component to update all quantities in the product dimensions matrix.
 * @param props - Enter all quantities component props.
 * @returns React node.
 */
export const ProductDimensionsMatrixEnterAllQuantitiesComponent: React.FC<IProductDimensionsMatrixEnterAllQuantitiesProps> = (
    props: IProductDimensionsMatrixEnterAllQuantitiesProps) => {

    const buttonTogglingReference = React.createRef<HTMLButtonElement>();
    const [isPopoverShown, setIsPopoverShown] = React.useState(false);

    const popoverClassName = `${props.className}__popover`;

    const togglePopoverHandler = React.useCallback(() => {
        setIsPopoverShown(!isPopoverShown);
    }, [isPopoverShown]);

    const handleApplyAll = React.useCallback((currentQuantity: number) => {
        setIsPopoverShown(false);
        props.onApplyToAll(currentQuantity);
    }, [props]);

    return (
        <div className={props.className}>
            <Button
                className={`${props.className}__toggle-button`}
                title={props.resources.productDimensionsMatrixEnterAllQuantitiesButtonText}
                innerRef={buttonTogglingReference}
                aria-expanded={isPopoverShown}
                aria-describedby={props.className}
                onClick={togglePopoverHandler}
            >
                {props.resources.productDimensionsMatrixEnterAllQuantitiesButtonText}
            </Button>
            <Popover
                className={popoverClassName}
                placement='bottom-start'
                isOpen={isPopoverShown}
                target={buttonTogglingReference}
                toggle={togglePopoverHandler}
            >
                <ProductDimensionsMatrixEnterAllQuantitiesPopoverContentComponent
                    {...props}
                    className={`${popoverClassName}__content`}
                    onApplyToAll={handleApplyAll}
                />
            </Popover>
        </div>
    );
};
